import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowBendUpLeft,
  CheckCircle,
  Circle,
  PaperPlaneTilt,
  Plus,
  Trash,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { Comment } from "../../types/document";
import { useCollaborationStore, type CommentFilter } from "../../stores/collaboration";

interface CommentThreadProps {
  comments: Comment[];
  documentId?: string;
  onAddComment?: (content: string, lineStart: number, lineEnd: number, parentId?: string) => void;
  onResolveComment?: (commentId: string) => void;
  onDeleteComment?: (commentId: string) => void;
  onReply?: (content: string, parentId: string) => void;
  onNavigateToLine?: (line: number) => void;
  className?: string;
}

const AVATAR_PALETTE = [
  "#3B82F6", "#8B5CF6", "#EC4899", "#06B6D4",
  "#22C55E", "#F59E0B", "#EF4444", "#F97316",
];

function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function getInitials(userId: string): string {
  if (!userId) return "?";
  return userId.slice(0, 2).toUpperCase();
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const FILTERS: { id: CommentFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
];

interface CommentItemProps {
  comment: Comment;
  replies: Comment[];
  isFocused: boolean;
  onFocus: (id: string) => void;
  onResolve?: (commentId: string) => void;
  onDelete?: (commentId: string) => void;
  onReply?: (content: string, parentId: string) => void;
  onNavigateToLine?: (line: number) => void;
  depth?: number;
}

function CommentItem({
  comment,
  replies,
  isFocused,
  onFocus,
  onResolve,
  onDelete,
  onReply,
  onNavigateToLine,
  depth = 0,
}: CommentItemProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused && depth === 0 && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isFocused, depth]);

  const handleReply = () => {
    if (!replyText.trim() || !onReply) return;
    onReply(replyText.trim(), comment.id);
    setReplyText("");
    setIsReplying(false);
  };

  const handleClick = () => {
    onFocus(comment.id);
    if (depth === 0) onNavigateToLine?.(comment.line_start);
  };

  const color = getUserColor(comment.user_id ?? "anon");
  const initials = getInitials(comment.user_id ?? "anon");

  return (
    <motion.article
      ref={itemRef}
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginTop: 0 }}
      transition={{ duration: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
      onClick={handleClick}
      className={cn(
        "group relative rounded-[var(--radius-sm)] px-2 py-1.5 cursor-pointer",
        "transition-colors duration-75",
        depth > 0 && "ml-5 pl-2.5",
        isFocused && depth === 0
          ? "bg-[var(--color-cool-azure)]/8"
          : "hover:bg-[var(--bg-surface-hover)]",
        comment.resolved && "opacity-60",
      )}
    >
      {/* Focus accent bar */}
      {isFocused && depth === 0 && (
        <motion.div
          layoutId="comment-focus-bar"
          className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full bg-[var(--color-cool-azure)]"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}

      {/* Nested reply border */}
      {depth > 0 && (
        <div className="absolute left-0 top-0 bottom-0 w-px bg-[var(--border-subtle)]" />
      )}

      <div className="flex items-start gap-2">
        {/* Avatar */}
        <div
          className="flex shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white select-none"
          style={{
            width: 18,
            height: 18,
            backgroundColor: color,
          }}
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-medium text-[var(--text-primary)] truncate">
              {comment.user_id ?? "Anonymous"}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToLine?.(comment.line_start);
              }}
              className={cn(
                "text-[9px] font-mono tabular-nums px-1 py-px rounded-[var(--radius-sm)]",
                "text-[var(--color-cool-azure)] bg-[var(--color-cool-azure)]/8",
                "hover:bg-[var(--color-cool-azure)]/15 transition-colors duration-75",
              )}
            >
              L{comment.line_start}
              {comment.line_start !== comment.line_end ? `–${comment.line_end}` : ""}
            </button>
            <span className="text-[10px] text-[var(--text-muted)]">
              {formatTimestamp(comment.created_at)}
            </span>
          </div>

          <p className="mt-0.5 text-[12px] text-[var(--text-secondary)] leading-relaxed">
            {comment.content}
          </p>

          {/* Reply count */}
          {replies.length > 0 && !isReplying && (
            <span className="mt-0.5 text-[10px] text-[var(--text-muted)]">
              {replies.length} {replies.length === 1 ? "reply" : "replies"}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-75">
          {onResolve && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResolve(comment.id);
              }}
              className={cn(
                "p-0.5 rounded-[var(--radius-sm)]",
                comment.resolved
                  ? "text-[var(--color-signal-green)]"
                  : "text-[var(--text-muted)] hover:text-[var(--color-signal-green)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
              )}
              title={comment.resolved ? "Unresolve" : "Resolve"}
            >
              {comment.resolved ? (
                <CheckCircle size={13} weight="fill" />
              ) : (
                <Circle size={13} />
              )}
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(comment.id);
              }}
              className={cn(
                "p-0.5 rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--color-soft-red)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
              )}
              title="Delete"
            >
              <Trash size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Reply input */}
      {!comment.resolved && onReply && depth === 0 && (
        <div className="mt-1 ml-[26px]">
          {!isReplying ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsReplying(true);
              }}
              className={cn(
                "flex items-center gap-1 text-[10px] text-[var(--text-muted)]",
                "hover:text-[var(--text-secondary)] transition-colors duration-75",
                "opacity-0 group-hover:opacity-100",
              )}
            >
              <ArrowBendUpLeft size={11} />
              Reply
            </button>
          ) : (
            <div
              className="mt-1 flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Reply..."
                autoFocus
                className={cn(
                  "flex-1 min-w-0 rounded-[var(--radius-sm)]",
                  "border border-[var(--border-hairline)]",
                  "bg-transparent px-2 py-1 text-[11px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)] outline-none",
                  "focus:border-[var(--color-cool-azure)]/40",
                  "transition-colors duration-100",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                  if (e.key === "Escape") {
                    setIsReplying(false);
                    setReplyText("");
                  }
                }}
              />
              <button
                onClick={handleReply}
                disabled={!replyText.trim()}
                className={cn(
                  "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
                  "text-[var(--text-muted)] hover:text-[var(--color-cool-azure)]",
                  "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                  "disabled:opacity-40 disabled:pointer-events-none",
                )}
              >
                <PaperPlaneTilt size={12} weight="fill" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Nested replies */}
      {replies.length > 0 && (
        <div className="mt-1.5 space-y-0.5 ml-[26px]">
          <AnimatePresence mode="popLayout">
            {replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                replies={[]}
                isFocused={false}
                onFocus={onFocus}
                onResolve={onResolve}
                onDelete={onDelete}
                onReply={onReply}
                onNavigateToLine={onNavigateToLine}
                depth={depth + 1}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.article>
  );
}

export function CommentThread({
  comments,
  documentId: _documentId,
  onAddComment,
  onResolveComment,
  onDeleteComment,
  onReply,
  onNavigateToLine,
  className,
}: CommentThreadProps) {
  const focusedCommentId = useCollaborationStore((s) => s.focusedCommentId);
  const setFocusedComment = useCollaborationStore((s) => s.setFocusedComment);
  const commentFilter = useCollaborationStore((s) => s.commentFilter);
  const setCommentFilter = useCollaborationStore((s) => s.setCommentFilter);

  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [newLineStart, setNewLineStart] = useState(1);
  const [newLineEnd, setNewLineEnd] = useState(1);

  const rootComments = useMemo(
    () => comments.filter((c) => !c.parent_id),
    [comments],
  );

  const getReplies = useCallback(
    (parentId: string) => comments.filter((c) => c.parent_id === parentId),
    [comments],
  );

  const filteredComments = useMemo(() => {
    if (commentFilter === "open") return rootComments.filter((c) => !c.resolved);
    if (commentFilter === "resolved") return rootComments.filter((c) => c.resolved);
    return rootComments;
  }, [rootComments, commentFilter]);

  const counts = useMemo(() => ({
    all: rootComments.length,
    open: rootComments.filter((c) => !c.resolved).length,
    resolved: rootComments.filter((c) => c.resolved).length,
  }), [rootComments]);

  const handleAddComment = () => {
    if (!newContent.trim() || !onAddComment) return;
    onAddComment(newContent.trim(), newLineStart, newLineEnd);
    setNewContent("");
    setNewLineStart(1);
    setNewLineEnd(1);
    setIsAdding(false);
  };

  const resetForm = () => {
    setIsAdding(false);
    setNewContent("");
    setNewLineStart(1);
    setNewLineEnd(1);
  };

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Comments
        </span>
        <div className="flex items-center gap-1">
          {counts.all > 0 && (
            <span className="text-[10px] tabular-nums text-[var(--text-muted)]">
              {counts.open}
            </span>
          )}
          {onAddComment && (
            <button
              onClick={() => setIsAdding(true)}
              className={cn(
                "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
              )}
              aria-label="New comment"
            >
              <Plus size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      {counts.all > 0 && (
        <div className="flex items-center gap-0.5 px-2.5 pb-1.5">
          {FILTERS.map((f) => {
            const count = counts[f.id];
            return (
              <button
                key={f.id}
                onClick={() => setCommentFilter(f.id)}
                className={cn(
                  "relative px-2 py-0.5 text-[10px] font-medium rounded-[var(--radius-sm)]",
                  "transition-colors duration-75",
                  commentFilter === f.id
                    ? "text-[var(--text-primary)] bg-[var(--bg-surface-hover)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
                )}
              >
                {f.label}
                {count > 0 && (
                  <span className="ml-1 tabular-nums opacity-60">{count}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Inline add form */}
        <AnimatePresence>
          {isAdding && onAddComment && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden px-2 mb-1"
            >
              <div className="flex flex-col gap-1.5 py-1">
                <input
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  placeholder="Write a comment..."
                  autoFocus
                  className={cn(
                    "w-full rounded-[var(--radius-sm)]",
                    "border border-[var(--color-cool-azure)]",
                    "bg-transparent px-2 py-1 text-[11px] text-[var(--text-primary)]",
                    "placeholder:text-[var(--text-muted)] outline-none",
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                    if (e.key === "Escape") resetForm();
                  }}
                />
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-[var(--text-muted)]">L</span>
                  <input
                    type="number"
                    min={1}
                    value={newLineStart}
                    onChange={(e) =>
                      setNewLineStart(Math.max(1, parseInt(e.target.value, 10) || 1))
                    }
                    className={cn(
                      "w-10 rounded-[var(--radius-sm)]",
                      "border border-[var(--border-hairline)]",
                      "bg-transparent px-1.5 py-0.5 text-[10px] text-[var(--text-primary)]",
                      "outline-none tabular-nums text-center",
                      "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                    )}
                  />
                  <span className="text-[10px] text-[var(--text-muted)]">–</span>
                  <input
                    type="number"
                    min={1}
                    value={newLineEnd}
                    onChange={(e) =>
                      setNewLineEnd(Math.max(1, parseInt(e.target.value, 10) || 1))
                    }
                    className={cn(
                      "w-10 rounded-[var(--radius-sm)]",
                      "border border-[var(--border-hairline)]",
                      "bg-transparent px-1.5 py-0.5 text-[10px] text-[var(--text-primary)]",
                      "outline-none tabular-nums text-center",
                      "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                    )}
                  />
                  <div className="flex-1" />
                  <button
                    onClick={handleAddComment}
                    disabled={!newContent.trim()}
                    className={cn(
                      "rounded-[var(--radius-sm)] px-2.5 py-1 text-[10px] font-medium",
                      "transition-all duration-100",
                      newContent.trim()
                        ? "bg-[var(--color-cool-azure)] text-white hover:brightness-110"
                        : "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                      "disabled:opacity-50 disabled:pointer-events-none",
                    )}
                  >
                    Add
                  </button>
                  <button
                    onClick={resetForm}
                    className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors duration-75"
                  >
                    Esc
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Comment list */}
        <div className="flex flex-col gap-0.5">
          <AnimatePresence mode="popLayout">
            {filteredComments.length === 0 && !isAdding ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-2 py-8 px-3"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]">
                  <ChatCircleDots
                    size={20}
                    weight="duotone"
                    className="text-[var(--text-muted)]"
                  />
                </div>
                <p className="text-[11px] text-[var(--text-muted)] text-center leading-relaxed">
                  {commentFilter === "all"
                    ? "No comments yet.\nSelect text to start a discussion."
                    : commentFilter === "open"
                      ? "No open comments."
                      : "No resolved comments."}
                </p>
              </motion.div>
            ) : (
              filteredComments
                .sort((a, b) => a.line_start - b.line_start)
                .map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    replies={getReplies(comment.id)}
                    isFocused={focusedCommentId === comment.id}
                    onFocus={setFocusedComment}
                    onResolve={onResolveComment}
                    onDelete={onDeleteComment}
                    onReply={onReply}
                    onNavigateToLine={onNavigateToLine}
                  />
                ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
